import axios from 'axios';

const API_BASE_URL =
  process.env.NODE_ENV === 'production'
    ? process.env.REACT_APP_API_URL || 'https://api.prepstop.com'
    : process.env.REACT_APP_API_URL; // empty → dev proxy

export interface PatientNotification {
  id: number;
  patient_id: number;
  type: string;
  title: string;
  message: string;
  is_read: boolean;
  metadata: Record<string, any> | null;
  createdAt: string;
  updatedAt: string;
}

export interface NotificationPage {
  notifications: PatientNotification[];
  pagination: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  };
}

class NotificationService {
  private getAuthHeaders() {
    const token = localStorage.getItem('authToken');
    return {
      headers: {
        'Content-Type': 'application/json',
        ...(token && { Authorization: `Bearer ${token}` }),
      },
      withCredentials: true,
    };
  }

  /**
   * Fetch paginated notifications for the authenticated patient.
   * @param page  1-based page number (default 1)
   * @param limit items per page (default 5)
   */
  async getNotifications(page = 1, limit = 5): Promise<NotificationPage> {
    const response = await axios.get<NotificationPage>(
      `${API_BASE_URL}/api/notifications?page=${page}&limit=${limit}`,
      this.getAuthHeaders()
    );
    return response.data;
  }

  /**
   * Get the unread notification count for the badge.
   */
  async getUnreadCount(): Promise<number> {
    const response = await axios.get<{ unreadCount: number }>(
      `${API_BASE_URL}/api/notifications/unread-count`,
      this.getAuthHeaders()
    );
    return response.data.unreadCount;
  }

  /**
   * Mark a single notification as read.
   */
  async markAsRead(id: number): Promise<void> {
    await axios.patch(
      `${API_BASE_URL}/api/notifications/${id}/read`,
      {},
      this.getAuthHeaders()
    );
  }

  /**
   * Mark all notifications as read.
   */
  async markAllAsRead(): Promise<void> {
    await axios.patch(
      `${API_BASE_URL}/api/notifications/read-all`,
      {},
      this.getAuthHeaders()
    );
  }
}

export const notificationService = new NotificationService();
export default notificationService;
